import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Live from '../config/live.json';
import Local from '../config/local.json';
import EditTeamModal from '../models/EditTeamModal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WeekTeamStandingsTable from "./WeekTeamStandingsTable";
import WeekSeasonHighScoresTable from "./WeekSeasonHighScoresTable";

const TeamStandings = () => {
    const [info, setInfo] = useState([]);
    const [weekName, setWeekName] = useState();
    const [highScores, setHighScores] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedMainInfoId, setSelectedMainInfoId] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState("");
    const [progress, setProgress] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const url = Live.REACT_APP_API_URL;

    const showToast = (message, type = "success") => {
        toast(message, { type });
    };

    useEffect(() => {
        fetchInfoData()
    }, []);

    const fetchInfoData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${url}/main-info`);
            if (!response.ok) throw new Error("Network response was not ok");
            const result = await response.json();
            setInfo(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleMainInfoChange = async (selectedId) => {
        try {
            const response = await fetch(`${url}/standings-with-roster?mainInformationId=${selectedId}`);
            if (!response.ok) throw new Error("Network response was not ok");
            const result = await response.json();
            setSelectedMainInfoId(selectedId);
            setFilteredData(result.data.standings)
            setHighScores(result.data.seasonHighScores)
            const week = info.find(item => item.id == selectedId)?.week_number;
            setWeekName(week)
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];

        if (selectedFile && selectedFile.type !== "application/pdf") {
            alert('Only PDF files are allowed.')
            setFile(null);
            return;
        }

        setFile(selectedFile);
    };

    const handleUpload = async () => {
        if (!file) return;

        setUploading(true);
        setUploadStatus("Processing started...");
        setError(null);
        setProgress(0);

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await fetch(`${url}/extract-pdf`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) throw new Error("Failed to process PDF");

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let done = false;
            let buffer = '';

            while (!done) {
                const { value, done: isDone } = await reader.read();
                done = isDone;

                if (value) {
                    buffer += decoder.decode(value, { stream: true });

                    // Process complete JSON objects
                    const lines = buffer.split('\n');
                    buffer = lines.pop(); // Keep incomplete line in buffer

                    for (const line of lines) {
                        if (line.trim()) {
                            try {
                                const json = JSON.parse(line);
                                processJsonChunk(json);
                            } catch (parseError) {
                                console.error('JSON Parse Error:', parseError);
                                // Potentially log or handle parsing errors
                            }
                        }
                    }
                }
            }

            // Process any remaining buffer
            if (buffer.trim()) {
                try {
                    const finalJson = JSON.parse(buffer);
                    processJsonChunk(finalJson);
                } catch (parseError) {
                    console.error('Final JSON Parse Error:', parseError);
                }
            }
        } catch (err) {
            console.error('Upload Error:', err);
            setError(err.message);
            setUploading(false);
        }
    };

    const processJsonChunk = (json) => {
        setUploadStatus(json.status || "Processing...");
        setProgress(json.progress || 0);

        if (json.status === "Complete") {
            setFile(null);
            setUploading(false);
            fetchInfoData();
        }
        if (json.status === "Error") {
            alert(json.error);
            setFile(null);
            setUploading(false);
        }
    };

    const handleEdit = (team) => {
        setSelectedTeam({
            ...team,
            bowler_details: team.bowler_details?.length
                ? team.bowler_details
                : [{ bowler_details: [] }]
        });
        setShowModal(true);
    };

    const handleBowlerChange = (index, field, value) => {
        const updatedBowlers = [...selectedTeam.bowler_details[0].bowler_details];
        updatedBowlers[index][field] = value;
        setSelectedTeam({
            ...selectedTeam,
            bowler_details: [{ ...selectedTeam.bowler_details[0], bowler_details: updatedBowlers }]
        });
    };

    const addBowler = () => {
        if (selectedTeam.bowler_details[0].bowler_details.length >= 5) {
            alert("You cannot add more than 5 bowlers.");
            return;
        }

        const newBowler = { name: "", avg: 0, hdcp: 0 };
        setSelectedTeam({
            ...selectedTeam,
            bowler_details: [{
                ...selectedTeam.bowler_details[0],
                bowler_details: [...selectedTeam.bowler_details[0].bowler_details, newBowler]
            }]
        });
    };

    const handleSave = async () => {
        if (!selectedTeam) return;
        try {
            const response = await fetch(`${url}/update-team`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(selectedTeam)
            });
            if (!response.ok) throw new Error("Failed to update team");
            setShowModal(false);
            showToast("✅ Team updated successfully!", "success");
            handleMainInfoChange(selectedMainInfoId);
        } catch (err) {
            showToast("❌ Error updating team: " + err.message, "error");
        }
    };

    const handleDelete = async (teamId) => {
        if (!window.confirm("Are you sure you want to delete this team and its roster?")) return;

        try {
            const response = await fetch(`${url}/delete-team/${teamId}`, {
                method: "DELETE",
            });

            const result = await response.json();
            if (result.success) {
                showToast("✅ Team delete successfully!", "success");
                handleMainInfoChange(selectedMainInfoId);
            } else {
                showToast("❌ Failed to delete team: " + result?.message, "error");
            }
        } catch (err) {
            showToast("❌ Error delete team: " + err.message, "error");
            console.error("Delete Error:", err);
        }
    };

    const handleWeekDelete = async (teamId) => {
        if (!window.confirm("Are you sure you want to delete this week?")) return;

        try {
            const response = await fetch(`${url}/delete-week/${teamId}`, {
                method: "DELETE",
            });

            const result = await response.json();
            if (result.data.success) {
                showToast("✅ Week delete successfully!", "success");
                setSelectedMainInfoId("")
                fetchInfoData();
            } else {
                showToast("❌ Failed to delete team: " + result?.data?.message, "error");
            }
        } catch (err) {
            showToast("❌ Error delete team: " + err.message, "error");
            console.error("Delete Error:", err);
        }
    };

    return (
        <>
            <div className="container mt-4">
                <h2 className="text-center mb-4 fw-bold display-6">🔥 300 League Championship</h2>
                <div className="mb-3 text-center">
                    <input type="file" className="form-control mb-2" onChange={handleFileChange} />
                    <button className="btn btn-primary" onClick={handleUpload} disabled={!file || uploading}>
                        {uploading ? "Uploading..." : "Upload PDF"}
                    </button>
                </div>

                {uploading && (
                    <div className="progress mb-4">
                        <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${progress}%` }}>
                            {uploadStatus} ({progress}%)
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-center mt-4">Loading data...</div>
                ) : error ? (
                    <div className="text-center text-danger">Error: {error}</div>
                ) : (
                    <div className="mb-4">
                        <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            <table className="table table-bordered">
                                <thead className="table-secondary">
                                    <tr>
                                        <th>Week</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        info.length === 0 ? (
                                            <tr>
                                                <td colSpan="2" className="text-center text-muted mt-4">📭 No week data found.</td>
                                            </tr>
                                        ) : (
                                            info.map((inItem) => {
                                                const isSelected = inItem.id === selectedMainInfoId;
                                                return (
                                                    <tr key={inItem.id} className={isSelected ? "fw-bold" : ""}>
                                                        <td>Week {inItem.week_number}</td>
                                                        <td>
                                                            <button
                                                                className={`btn btn-sm me-2 ${isSelected ? "btn-success" : "btn-primary"}`}
                                                                onClick={() => handleMainInfoChange(inItem.id)}
                                                            >
                                                                {isSelected ? "✓ Selected" : "🔍 View"}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleWeekDelete(inItem.id)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <WeekTeamStandingsTable
                    selectedMainInfoId={selectedMainInfoId}
                    filteredData={filteredData}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                    setSelectedMainInfoId={setSelectedMainInfoId}
                    weekName={weekName}
                />

                <WeekSeasonHighScoresTable
                    data={highScores}
                    selectedMainInfoId={selectedMainInfoId}
                    url={url}
                    handleMainInfoChange={handleMainInfoChange}
                    showToast={showToast}
                />

                <EditTeamModal
                    selectedTeam={selectedTeam}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    handleSave={handleSave}
                    handleBowlerChange={handleBowlerChange}
                    addBowler={addBowler}
                    setSelectedTeam={setSelectedTeam}
                />

            </div>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
        </>
    );
};

export default TeamStandings;